import { motion } from 'framer-motion';
import BLOCKS, { BLOCK_ORDER } from '../data/blocks';
import { getElementsByBlock } from '../data/elements';

export default function BlocksOverview({ onElementClick }) {
  return (
    <div className="max-w-7xl mx-auto px-4">
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
          The <span className="text-gradient">11 Blocks</span> of AI
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Each block represents a functional category in the AI ecosystem. 
          Click to explore the elements within each block.
        </p>
      </motion.div>

      {/* Block Diagram Image */}
      <motion.div
        className="mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <img
          src="/images/Block_Diagram_Overview.png"
          alt="AI Periodic Table Block Overview"
          className="w-full max-w-4xl mx-auto rounded-xl border border-white/10"
        />
      </motion.div>

      {/* Blocks Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {BLOCK_ORDER.map((blockId, index) => {
          const block = BLOCKS[blockId];
          const elements = getElementsByBlock(blockId);
          
          return (
            <motion.div
              key={blockId}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="group p-6 rounded-2xl bg-dark-800/50 border border-white/5 hover:border-white/10 transition-all duration-300 hover:-translate-y-1"
              style={{
                background: `linear-gradient(135deg, ${block.color}08 0%, transparent 100%)`,
              }}
            >
              {/* Header */}
              <div className="flex items-center gap-4 mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${block.color}20` }}
                >
                  {block.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-mono px-2 py-0.5 rounded"
                      style={{ backgroundColor: block.color, color: '#000' }}
                    >
                      {block.code}
                    </span>
                    <span className="text-sm text-gray-500">Block {block.number}</span>
                  </div>
                  <h3 className="font-bold" style={{ color: block.color }}>
                    {block.name}
                  </h3>
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                {block.description}
              </p>

              {/* Element Count */}
              <div className="flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-sm text-gray-500">
                  {elements.length} elements
                </span>
                <div className="flex -space-x-1">
                  {elements.slice(0, 5).map((el, i) => (
                    <div
                      key={el.symbol}
                      className="w-6 h-6 rounded text-[8px] font-bold flex items-center justify-center border"
                      style={{
                        backgroundColor: `${block.color}30`,
                        borderColor: `${block.color}50`,
                        color: block.color,
                        zIndex: 5 - i,
                      }}
                    >
                      {el.symbol}
                    </div>
                  ))}
                  {elements.length > 5 && (
                    <div
                      className="w-6 h-6 rounded text-[8px] flex items-center justify-center bg-dark-700 border border-white/10 text-gray-400"
                    >
                      +{elements.length - 5}
                    </div>
                  )}
                </div>
              </div>

              {/* Speculative Badge */}
              {block.isSpeculative && (
                <div className="mt-4">
                  <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-500">
                    ✧ SPECULATIVE / THEORETICAL
                  </span>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Legend Image */}
      <motion.div
        className="mt-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <img
          src="/images/ai_periodic_table_legend.png"
          alt="AI Periodic Table Legend"
          className="w-full max-w-3xl mx-auto rounded-xl border border-white/10"
        />
      </motion.div>
    </div>
  );
}
